import React, { useCallback } from "react";
import CardFinder, { Art } from "../cards/CardFinder";

/**
 * Building a stack by picking cards rather than typing them.
 *
 * The box that used to be typed into is a list of what you have added, and the
 * search sits under it — you look a card up, press add, and it appears above.
 * Which is what people were doing anyway; the difference is that the app now
 * knows it picked a real card instead of hoping a line of text resolves to one.
 *
 * A quantity goes up and down with plus and minus, and minus at one takes the
 * card out. There is no separate remove: reaching zero is what removing is.
 *
 * The finding half lives in CardFinder, because submissions wanted the same
 * thing and the awkward parts — two games, missing art — are not stack-shaped.
 */

export default function StackBuilder({ items, onAdd, onBump, isOnePiece }) {
  const has = useCallback((key) => items.some((i) => i.key === key), [items]);

  return (
    <>
      {/* ── what is in the stack ── */}
      {items.length === 0 ? (
        <p className="sbd-empty">
          Nothing in the stack yet. Search below and add a card — it will show up here.
        </p>
      ) : (
        <ul className="sbd-list">
          {items.map((it) => (
            <li key={it.key} className={`sbd-item${it.unpriced ? " is-unpriced" : ""}`}>
              <Art src={it.image} className="sbd-item-art" name={it.name} />
              <span className="sbd-item-text">
                {it.name}
                <span className="sbd-item-set">
                  {it.set}
                  {it.number && !isOnePiece && ` · ${it.number}`}
                </span>
                {/* Said here, when it is added, rather than after pricing — the
                    search offers prints the price source has never listed, and
                    finding that out at the total is finding it out too late. */}
                {it.unpriced && (
                  <span className="sbd-item-warn">
                    no market price — add it manually below to count it
                  </span>
                )}
              </span>
              {/* Minus at one takes the card out. Reaching zero is what
                  removing is, so there is no second control for it. */}
              <span className="sbd-qty">
                <button
                  type="button"
                  onClick={() => onBump(it.key, -1)}
                  aria-label={it.qty > 1 ? `One fewer ${it.name}` : `Take ${it.name} out`}
                >
                  −
                </button>
                <b>{it.qty}</b>
                <button
                  type="button"
                  onClick={() => onBump(it.key, 1)}
                  aria-label={`One more ${it.name}`}
                >
                  +
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* ── finding the next one ── */}
      <CardFinder isOnePiece={isOnePiece} onPick={onAdd} isPicked={has} />
    </>
  );
}
